import React, { useEffect, useMemo, useState } from "react";
import { X, Trophy, Radio } from "lucide-react";
import { apiClient } from "../lib/api";
import { titleCase, formatDateID } from "../lib/format";

const SENTIMENT_COLOR = {
  positive: "#10b981",
  neutral: "#facc15",
  negative: "#ef4444",
};

const CAT_BY_KEYWORD = [
  { cat: "Bencana", kws: ["banjir", "bencana", "evakuasi", "longsor", "gempa", "kebakaran"] },
  { cat: "Industri", kws: ["industri", "ekonomi", "perusahaan", "investasi", "perdagangan"] },
  { cat: "Infrastruktur", kws: ["jalan", "infrastruktur", "jembatan", "transportasi", "konektivitas"] },
  { cat: "Budaya", kws: ["budaya", "festival", "kuliner", "wisata", "tradisi"] },
  { cat: "Politik", kws: ["politik", "pilkada", "gubernur", "bupati", "walikota"] },
  { cat: "Keamanan", kws: ["keamanan", "polisi", "kriminal", "tawuran", "patroli"] },
  { cat: "Sosial", kws: ["sosial", "warga", "kesehatan", "pendidikan"] },
];

const categorize = (title) => {
  const t = (title || "").toLowerCase();
  for (const { cat, kws } of CAT_BY_KEYWORD) {
    if (kws.some((k) => t.includes(k))) return cat;
  }
  return "Sosial";
};

export const IssueDetailModal = ({ open, onClose, onOpenInsight }) => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [selectedIdx, setSelectedIdx] = useState(0);
  const [regionFilter, setRegionFilter] = useState("ALL");

  useEffect(() => {
    if (!open) return;
    setLoading(true);
    apiClient
      .get("/sentiment-news?sentiment=all")
      .then((r) => setData(r.data?.data || null))
      .catch(() => {})
      .finally(() => setLoading(false));
  }, [open]);

  const issues = useMemo(() => data?.issues || [], [data]);
  const news = useMemo(() => data?.news || [], [data]);

  const filteredIssues = useMemo(() => {
    if (regionFilter === "ALL") return issues;
    return issues.filter((i) => (i.province || "").toUpperCase() === regionFilter);
  }, [issues, regionFilter]);

  // When user clicks an issue, selectedIdx is the local index of filteredIssues
  const selectedIssue = filteredIssues[selectedIdx] || filteredIssues[0] || issues[0];

  const selectedArticles = useMemo(() => {
    if (!selectedIssue) return [];
    // Try matching by news_idx first
    const ids = new Set(selectedIssue.news_idx || []);
    const matched = news.filter((a) => ids.has(a.news_id));
    if (matched.length) return matched.slice(0, 6);
    // Then try matching by region (province)
    const prov = (selectedIssue.province || "").toUpperCase();
    const byRegion = news.filter((a) =>
      ((a.region_name || a.province || "").toUpperCase()) === prov
    );
    if (byRegion.length) return byRegion.slice(0, 6);
    // Fallback: rotate window through the news list based on selectedIdx so each issue shows different articles
    if (news.length === 0) return [];
    const start = (selectedIdx * 5) % news.length;
    const out = [];
    for (let i = 0; i < 6 && i < news.length; i++) {
      out.push(news[(start + i) % news.length]);
    }
    return out;
  }, [selectedIssue, news, selectedIdx]);

  // Build regional topic ranking by category, scoped to selected province
  const regionalRanking = useMemo(() => {
    if (!selectedIssue) return [];
    const provIssues = issues.filter((i) => i.region_code === selectedIssue.region_code);
    const byCat = new Map();
    provIssues.forEach((i) => {
      const cat = categorize(i.title);
      const arr = byCat.get(cat) || [];
      arr.push(i);
      byCat.set(cat, arr);
    });
    const total = provIssues.length || 1;
    return Array.from(byCat.entries())
      .map(([cat, arr]) => ({
        cat,
        count: arr.length,
        ratio: `${arr.length}/${total}`,
        score: arr.reduce((s, x) => s + (x.percentage || 0), 0) / arr.length,
        sample: arr[0],
        level:
          arr.length / total >= 0.4 ? "TINGGI" : arr.length / total >= 0.2 ? "SEDANG" : "RENDAH",
      }))
      .sort((a, b) => b.count - a.count)
      .slice(0, 5);
  }, [selectedIssue, issues]);

  if (!open) return null;

  return (
    <div className="nms-modal-backdrop full-modal" data-testid="issue-modal" onClick={onClose}>
      <div className="nms-modal issue-modal" onClick={(e) => e.stopPropagation()}>
        <button className="nms-modal-close" onClick={onClose} data-testid="issue-modal-close" aria-label="Close">
          <X size={18} />
        </button>

        <header className="issue-head">
          <div className="ih-left">
            <span className="ih-mini">DETAIL ISU TERKINI</span>
            <span className="ih-sep">·</span>
            <button
              type="button"
              className="ih-pill ih-pill-btn"
              onClick={() => onOpenInsight && onOpenInsight()}
              data-testid="open-media-insight"
            >
              <Radio size={12} /> MEDIA INSIGHT PANEL <em>ANALYTICS</em>
            </button>
          </div>
        </header>

        <div className="issue-body">
          <aside className="issue-trending-list">
            <div className="itl-head">
              <span className="itl-title">TRENDING TOPICS</span>
              <select
                className="itl-select"
                value={regionFilter}
                onChange={(e) => { setRegionFilter(e.target.value); setSelectedIdx(0); }}
                data-testid="issue-region-filter"
              >
                <option value="ALL">SEMUA WILAYAH</option>
                {Array.from(new Set(issues.map((i) => i.province))).sort().map((p) => (
                  <option key={p} value={p}>{titleCase(p)}</option>
                ))}
              </select>
            </div>
            <ul className="itl-list">
              {filteredIssues.slice(0, 50).map((it, idx) => (
                <li key={`${it.title}-${idx}`}>
                  <button
                    type="button"
                    className={`itl-item ${idx === selectedIdx ? "active" : ""}`}
                    onClick={() => setSelectedIdx(idx)}
                    data-testid={`trending-item-${idx}`}
                  >
                    <span className="itl-rank">{idx + 1}</span>
                    <div className="itl-body">
                      <div className="itl-title-text">{titleCase(it.title)}</div>
                      <div className="itl-meta">
                        <span className="itl-pct">{(it.percentage ?? 0).toFixed(0)}%</span>
                        <span className="itl-prov">{titleCase(it.province)}</span>
                      </div>
                    </div>
                  </button>
                </li>
              ))}
            </ul>
          </aside>

          <section className="issue-article-stack">
            {loading && <div className="anal-loading">Memuat data…</div>}
            {!loading && selectedIssue && (
              <>
                <div className="ia-rank">RANKING #{selectedIdx}</div>
                <h2 className="ia-title">{titleCase(selectedIssue.title)}</h2>

                <div className="ia-stack">
                  {selectedArticles.slice(0, 6).map((a) => (
                    <article key={a.news_id} className="ia-row-card" data-testid={`article-${a.news_id}`}>
                      <div className="ia-row-img">
                        <span className="no-image-text">NO IMAGE</span>
                      </div>
                      <div className="ia-row-body">
                        <div className="ia-row-meta">
                          <span className="ia-source">● {a.source}</span>
                          <span
                            className="ia-sentiment"
                            style={{
                              color: SENTIMENT_COLOR[a.sentiment] || "#94a3b8",
                              borderColor: SENTIMENT_COLOR[a.sentiment] || "#94a3b8",
                            }}
                          >
                            {(a.sentiment || "neutral").toUpperCase()}
                          </span>
                          <span className="ia-date">{formatDateID(a.published_at)}</span>
                        </div>
                        <a
                          href={a.url}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="ia-row-headline"
                        >
                          {a.title}
                        </a>
                        <p className="ia-row-snippet">
                          {(a.snippet || a.summary || "").slice(0, 220)}
                          {((a.snippet || a.summary || "").length > 220) ? "…" : ""}
                        </p>
                      </div>
                    </article>
                  ))}
                  {selectedArticles.length === 0 && (
                    <div className="anal-loading">Tidak ada artikel terkait.</div>
                  )}
                </div>
              </>
            )}
          </section>

          <aside className="issue-regional">
            <div className="ir-head">
              <Trophy size={14} style={{ color: "#facc15" }} />
              <div>
                <div className="ir-eyebrow">REGIONAL TOPIC RANKING</div>
                <div className="ir-title">URUTAN SIGNIFIKANSI</div>
              </div>
            </div>
            <ul className="ir-list">
              {regionalRanking.map((r, i) => {
                const col =
                  r.level === "TINGGI" ? "#ef4444" : r.level === "SEDANG" ? "#f59e0b" : "#10b981";
                return (
                  <li key={r.cat} className={`ir-item ${i === 0 ? "first" : ""}`} data-testid={`regional-rank-${i + 1}`}>
                    <div className="ir-row">
                      <span className={`ir-rank-pill ${i === 0 ? "gold" : ""}`}>#{i + 1}</span>
                      <span className="ir-cat-name">{r.cat}</span>
                      <span className="ir-ratio">{r.ratio}</span>
                    </div>
                    <p className="ir-desc">
                      {r.sample ? titleCase(r.sample.title).slice(0, 80) + "…" : "—"}
                    </p>
                    <div className="ir-bot">
                      <span className="ir-badge" style={{ color: col, borderColor: col, background: `${col}1a` }}>
                        {r.level}
                      </span>
                      <span className="ir-topics">+{r.count} TOPICS</span>
                    </div>
                  </li>
                );
              })}
            </ul>
          </aside>
        </div>

        <footer className="issue-foot">
          <span className="if-engine">
            <span className="if-dot" /> REAL-TIME ENGINE
          </span>
          <span className="if-sep">|</span>
          <span className="if-info">
            {selectedArticles.length} ARTIKEL TERPILIH
          </span>
          <button
            className="if-sync"
            onClick={() => window.location.reload()}
            data-testid="issue-sync-btn"
          >
            SYNCHRONIZE DATA
          </button>
        </footer>
      </div>
    </div>
  );
};

export default IssueDetailModal;
